import { computed, Ref, ComputedRef } from "vue";
import * as yup from "yup";
import { FormOption, FormField } from "@/types/form";
import { CertificationVM } from "@/types/certifications";
import {
  DisbursementFormVM,  
  DisbursementVM,
  DisbursementStatusIds,
} from "@/types/disbursements";
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';

export interface ValidationData {
  certification: ComputedRef<CertificationVM>;
  disbursement: Ref<DisbursementVM>;
  disbursementIndex: Ref<number>;
  formData: Ref<DisbursementFormVM | null>;
  mode: Ref<"view" | "edit" | "add">;
  disbursementForms: Ref<DisbursementVM[]>;
}

export function getFilteredDisbursementStatusOptions(data: ValidationData) {
  const disbursementStatuses = computed(() => {
    return data.mode.value === "add"
      ? data.certification.value?.disbursementFormOptions?.disbursementStatusTypes.filter(
          (fo: FormOption) => fo.showOnAdd
        )
      : data.certification.value?.disbursementFormOptions?.disbursementStatusTypes.filter(
          (fo: FormOption) => fo.showOnEdit
        );
  });

  return disbursementStatuses;
}

export function useDisbursementForm(data: ValidationData) {
  const store = useDisbursementsStore();
  const formFields = computed(() => {
    const fields = [
      {
        name: "sequenceNumber",
        label: "Sequence",
        type: "string",
        value: data.formData.value?.sequenceNumber,
        width: "col-4",
        readonly: true,
        showOnEdit: true,
        showOnAdd: true,
        showOnView: true,
        disabledOnEdit: true,
        hideLabel: true,
      },
      {
        name: "disbursementStatusTypeId",
        label: "Disb. Status",
        type: "select",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.disbursementStatusTypeId
            : data.formData.value?.disbursementStatusTypeName,
        options: getDisbursementStatuses(data),
        readonly: data.formData.value?.isDisbursed,
        showOnEdit: true,
        showOnAdd: true,
        showOnView: true,
        disabledOnEdit: false,
        hideLabel: true,
        width: "175px",
        tooltip:
          data.formData.value?.disbursementStatusTypeId ===
            DisbursementStatusIds.CERTIFIED_ON_HOLD &&
          data.formData.value?.certificationDisbursementStatusReasonTypeId !=
            null,
        tooltipText:
          data.formData.value?.certificationDisbursementStatusReasonTypeId !=
          null
            ? data.formData.value?.certificationDisbursementStatusReasonTypeName
            : null,
      },
      {
        label: "Disb. Amount",
        name: "disbursementAmount",
        type: "money",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.disbursementAmount
            : data.formData.value?.disbursementAmountDisplay,
        displayValueKey: "disbursementAmountDisplay",
        readonly: data.formData.value?.isDisbursed,
        showOnAdd: true,
        showOnEdit: true,
        showOnView: true,
        disabledOnEdit: false,
        hideLabel: true,
      },
      {
        label: "Disb. Date",
        name: "disbursementDate",
        type: "date",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.disbursementDate
            : data.formData.value?.disbursementDateDisplay,
        displayValueKey: "disbursementDateDisplay",
        readonly: data.formData.value?.isDisbursed,
        showOnAdd: true,
        showOnEdit: true,
        showOnView: true,
        disabledOnEdit: false,
        hideLabel: true,
      },
      {
        label: "Disb. Method",
        title: "Disbursement Method",
        name: "disbursementMethodTypeId",
        value: data.formData.value?.disbursementMethodTypeName,
        displayValueKey: "disbursementMethodTypeName",
        readonly: true,
        showOnAdd: true,
        showOnEdit: true,
        showOnView: true,
        disabledOnEdit: true,
        disabledOnAdd: true,
        type: "select",
        options:
          data.certification.value?.disbursementFormOptions
            ?.disbursementMethodTypes,
        hideLabel: true,
      },
    ] as FormField[];

    return fields.filter((field) => {
      if (data.mode.value === "edit") {
        return field.showOnEdit !== false;
      }

      if (data.mode.value === "add") {
        return field.showOnAdd !== false;
      }

      return fields.filter((field) => {
        return field.showOnView === true;
      });
    });
  });

  const formSchema = computed(() => {
    const originalDate = data.mode.value !== 'add' 
      ? DisbHelpers.formatDateYYYYMMDD(data.disbursement.value.details.disbursementDate)
      : null;    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return yup.object().shape({
      disbursementAmount: yup
        .number()
        .required("Disb. Amount is required")
        .min(50, "Disb. Amount must be at least $50")
        .test("valid-disbursement-amount", "", function (value, testContext) {
          const errors: yup.ValidationError[] = [];
          if (value === undefined || value === null || value < 0) {
            errors.push(
              testContext.createError({
                path: testContext.path,
                message: "Disb. Amount is required",
              })
            );
          }
          else {            
            store.updateFormAmount(data.disbursement.value.details.id, value);            
            const total = DisbHelpers.calculateTotalDisbursementAmount();
            const certifiedAmount = (data.certification.value.certificationAmountForDisbursements);
            if (total > certifiedAmount) {              
              errors.push(
                testContext.createError({
                  path: testContext.path,
                  message: `Sum of Disb. Amounts cannot exceed ${DisbHelpers.formatCurrency(certifiedAmount)}`,
                })
              );
            }
          }

          if (errors.length > 0) {
            throw new yup.ValidationError(errors);
          }

          return true;
        }),
      disbursementDate: yup
        .date()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .required("Disb. Date is required")       
        .test(
          'not-in-past',
          'Disb. Date cannot be in the past',
          function(value) {
            if (!value) return false;            
            const valueStr = DisbHelpers.formatDateYYYYMMDD(value);            
            if (originalDate && originalDate === valueStr) {
              return true;
            }            
            const valueDate = DisbHelpers.parseDateString(valueStr);            
            return valueDate >= today;
          }
        ),
    });
  });

  const validationSchema = computed(() => {
    switch (data.mode.value) {
      case "edit":
      case "add":
        return formSchema.value;
      default:
        return yup.object({});
    }
  });

  function getDisbursementStatuses(data : ValidationData) {
    if (data.mode.value === "add") {
      return data.certification.value?.disbursementFormOptions?.disbursementStatusTypes.filter(
          (fo: FormOption) => fo.showOnAdd
        );
    }

    return data.disbursement.value?.formOptions?.disbursementStatusTypes.filter(
      (fo: FormOption) => fo.showOnEdit
    );
  }

  function getDisbursementFormChanges(
    initialValues: DisbursementFormVM,
    formData: Ref<DisbursementFormVM>,
    formFields: FormField[]
  ) {
    const changes = [
      {
        op: "add",
        path: "/disbursementStatusTypeId",
        value: formData.value?.disbursementStatusTypeId,
      },
      {
        op: "add",
        path: "/disbursementAmount",
        value: formData.value?.disbursementAmount,
      },
      {
        op: "add",
        path: "/disbursementDate",
        value: formData.value?.disbursementDate,
      },
      {
        op: "add",
        path: "/disbursementMethodTypeId",
        value: initialValues?.disbursementMethodTypeId,
      },
    ];

    return changes;
  }

  return {
    formFields,
    validationSchema,
    formSchema,
    getDisbursementFormChanges,    
  };
}
